'use client';

import { getApiUrl } from '@/lib/apiConfig';
import { isCapacitor } from '@/lib/platformUtils';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import {
  getOperationsMyFeaturesFetchInit,
  getOperationsMyFeaturesPath,
} from '@/lib/operations/myFeaturesClient';

const OPERATIONS_MY_FEATURES_CACHE_TTL_MS = 30 * 1000;
/** Web: keep UI snappy. Native: mobile radios / WebView cold-start often exceed 8s. */
const OPERATIONS_MY_FEATURES_FETCH_TIMEOUT_MS = 8_000;
const OPERATIONS_MY_FEATURES_FETCH_TIMEOUT_MS_CAPACITOR = 45_000;

function shouldLogSmartTaskBootstrapDiagnostics(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isCapacitor()) return true;
  return (window as unknown as Record<string, unknown>).__coldStartDiagnosticsEnabled === true;
}

function getDefaultMyFeaturesTimeoutMs(): number {
  if (typeof window === 'undefined') return OPERATIONS_MY_FEATURES_FETCH_TIMEOUT_MS;
  return isCapacitor()
    ? OPERATIONS_MY_FEATURES_FETCH_TIMEOUT_MS_CAPACITOR
    : OPERATIONS_MY_FEATURES_FETCH_TIMEOUT_MS;
}

type OperationsMyFeaturesCacheEntry = {
  expiresAt: number;
  promise?: Promise<Record<string, unknown> | null>;
  value?: Record<string, unknown> | null;
};

const operationsMyFeaturesCache = new Map<string, OperationsMyFeaturesCacheEntry>();

export async function fetchOperationsMyFeatures(
  accessToken: string,
  options: {
    forceRefresh?: boolean;
    timeoutMs?: number;
  } = {},
): Promise<Record<string, unknown> | null> {
  if (!accessToken) {
    return null;
  }

  const currentClientId = typeof window !== 'undefined'
    ? window.localStorage.getItem('current_client_id') || 'no-client'
    : 'no-client';
  const cacheKey = `${accessToken}:${currentClientId}`;
  const now = Date.now();
  const cachedEntry = operationsMyFeaturesCache.get(cacheKey);

  if (!options.forceRefresh && cachedEntry) {
    if (cachedEntry.promise) {
      return cachedEntry.promise;
    }

    if (cachedEntry.expiresAt > now && cachedEntry.value !== undefined) {
      return cachedEntry.value;
    }
  }

  const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutMs = Math.max(1_000, options.timeoutMs ?? getDefaultMyFeaturesTimeoutMs());
  const timeoutId = abortController
    ? window.setTimeout(() => {
        try {
          abortController.abort(
            new DOMException(`operations/my-features exceeded ${timeoutMs}ms`, 'TimeoutError'),
          );
        } catch {
          abortController.abort();
        }
      }, timeoutMs)
    : null;

  const requestPromise = fetch(
    getApiUrl(getOperationsMyFeaturesPath()),
    getOperationsMyFeaturesFetchInit(accessToken, {
      headers: {
        'Content-Type': 'application/json',
      },
      ...(abortController ? { signal: abortController.signal } : {}),
    }),
  )
    .then(async (response) => {
      if (!response.ok) {
        return null;
      }

      const data = await response.json().catch(() => null);
      return data && typeof data === 'object'
        ? data as Record<string, unknown>
        : null;
    })
    .catch((error) => {
      const name = error instanceof Error ? error.name : '';
      if (name === 'AbortError' || name === 'TimeoutError') {
        if (shouldLogSmartTaskBootstrapDiagnostics()) {
          console.debug(
            '[SmartTaskBootstrap] operations/my-features timed out or aborted (using cache/offline if available)',
            error instanceof Error ? error.message : error,
          );
        }
        return null;
      }
      if (!isSilentFetchError(error)) console.warn('[SmartTaskBootstrap] Failed to load operations/my-features:', error);
      return null;
    })
    .finally(() => {
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    });

  operationsMyFeaturesCache.set(cacheKey, {
    expiresAt: now + OPERATIONS_MY_FEATURES_CACHE_TTL_MS,
    promise: requestPromise,
  });

  const value = await requestPromise;

  operationsMyFeaturesCache.set(cacheKey, {
    expiresAt: Date.now() + OPERATIONS_MY_FEATURES_CACHE_TTL_MS,
    value,
  });

  return value;
}
